import maplibregl from 'maplibre-gl'
import { createVNode, render, type Component } from 'vue'

import { Z_INDEX } from '@/utils/zIndex'

/**
 * Creates a `maplibregl.Marker` with the project default of **no terrain-occlusion fade**. MapLibre
 * defaults `opacityWhenCovered` to `'0.2'`, which greys out HTML markers that sit behind 3D terrain
 * (the Relief layer) relative to the camera. We control marker visibility explicitly elsewhere, so
 * markers should always render at full opacity. Use this instead of `new maplibregl.Marker(...)` for
 * every map marker. Callers can still override any option (including `opacityWhenCovered`).
 */
export const createMapMarker = (options: maplibregl.MarkerOptions): maplibregl.Marker =>
  new maplibregl.Marker({ opacityWhenCovered: '1', ...options })

/**
 * Builds the DOM for a standard map point marker — a 36px filled circle with a 2px white ring, drop
 * shadow and a white 24px category icon, plus a 48px invisible touch area for comfortable tapping.
 * The single source of truth for the Apple Maps–style marker so every marker type (route waypoints,
 * saved collection POIs) stays pixel-identical; only the icon, fill colour and container class differ.
 *
 * Returns the outer `element` (give to `maplibregl.Marker`), the `touchArea` (attach click/hover/
 * touch handlers here) and the `markerEl` (apply hover/selected `scale()` transforms here).
 */
export const createMapMarkerElement = (
  iconComponent: Component,
  markerColor: string,
  containerClass: string,
): { element: HTMLDivElement; touchArea: HTMLDivElement; markerEl: HTMLDivElement } => {
  const container = document.createElement('div')
  container.className = `${containerClass} relative`
  container.style.zIndex = Z_INDEX.MAP_MARKERS_WAYPOINT.toString()

  // Invisible touch-friendly area for easier clicking (48x48px minimum touch target)
  // Centered on the marker for consistent touch interaction
  const touchArea = document.createElement('div')
  touchArea.className = 'touch-area'
  touchArea.style.position = 'absolute'
  touchArea.style.width = '48px'
  touchArea.style.height = '48px'
  touchArea.style.top = '-24px' // Half of touch area size to center it
  touchArea.style.left = '-24px' // Half of touch area size to center it
  touchArea.style.borderRadius = '50%'
  touchArea.style.cursor = 'pointer'
  touchArea.style.background = 'transparent'

  // Outer circle background - positioned at center (0,0) of container
  const markerEl = document.createElement('div')
  markerEl.style.position = 'absolute'
  markerEl.style.top = '-18px' // Half of marker size (36px/2 = 18px) to center it
  markerEl.style.left = '-18px' // Half of marker size (36px/2 = 18px) to center it
  markerEl.className =
    'flex h-[36px] w-[36px] items-center justify-center rounded-full border-2 border-white text-white shadow-lg pointer-events-none'
  markerEl.style.backgroundColor = markerColor

  // Create the icon element
  const iconEl = document.createElement('div')
  iconEl.className = 'text-white scale-90'

  // Create a Vue node from the provided Lucide icon component and render it to the DOM
  const vNode = createVNode(iconComponent, {
    size: 24,
    stroke: 'currentColor',
    strokeWidth: 2,
    absoluteStrokeWidth: true,
  })
  render(vNode, iconEl)

  // Append the icon to the marker
  markerEl.appendChild(iconEl)
  container.appendChild(touchArea)
  container.appendChild(markerEl)

  return { element: container, touchArea, markerEl }
}
